import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ChangeView {
  readonly changeId: string;
  readonly changeCode: string;
  readonly title: string;
  readonly description: string | null;
  readonly changeType: string;
  readonly changeCategory: string;
  readonly status: string;
  readonly priority: string;
  readonly riskRating: string | null;
  readonly isEmergency: boolean;
  readonly rollbackPlan: string | null;
  readonly scheduledStartAt: string | null;
  readonly scheduledEndAt: string | null;
}

interface ApprovalRow {
  readonly approvalId: string;
  readonly approvalCode: string;
  readonly approvalLevel: string;
  readonly status: string;
  readonly decisionComment: string | null;
}

/**
 * GR_M31-M35 §L32.05 / §M — change request detail viewer
 * (`/admin/devops/changes/:id`). Renders a single change request (T419) with its
 * implementation window (§M L32.05 change.window_start → scheduled_start_at) and
 * derived risk rating (§M change.risk_level → risk_rating), plus its rollback
 * plan and the emergency flag that drives the BR-02 retrospective-review path.
 * The §22 approval workflow (SoD) is actionable here: pending approval gates can
 * be approved or rejected via the guarded /v1/devops/change-approvals write API.
 */
export function ChangeDetailPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [change, setChange] = useState<ChangeView | null>(null);
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [comment, setComment] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m32.changeDetail.noId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: ChangeView }>(`/v1/devops/changes/${id}`, { token })
      .then(async (r) => {
        if (!alive) return;
        setChange(r.data);
        setError(null);
        try {
          const ap = await apiRequest<{ data: ApprovalRow[] }>(`/v1/devops/change-approvals?changeId=${id}`, { token });
          if (alive) setApprovals(ap.data ?? []);
        } catch {
          if (alive) setApprovals([]);
        }
      })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m32.changeDetail.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m32.changeDetail.authError')
      : (err as Error).message;

  const decide = async (a: ApprovalRow, path: 'approve' | 'reject', label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/change-approvals/${a.approvalId}/${path}`, {
        method: 'POST',
        body: { decisionComment: (comment[a.approvalId] ?? '').trim() || undefined },
        token,
      });
      setNotice(t('m32.changeDetail.decisionNotice', { code: a.approvalCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  if (loading) return <main aria-label="change-detail"><p>{t('m32.changeDetail.loading')}</p></main>;
  if (error) return <main aria-label="change-detail"><p role="alert">{error}</p></main>;
  if (!change) return <main aria-label="change-detail"><p role="alert">{t('m32.changeDetail.notFound')}</p></main>;

  return (
    <main aria-label="change-detail">
      <h1>{change.title}</h1>
      <dl>
        <dt>{t('m32.changeDetail.code')}</dt><dd aria-label="change-code">{change.changeCode}</dd>
        <dt>{t('m32.changeDetail.type')}</dt><dd aria-label="change-type">{change.changeType}</dd>
        <dt>{t('m32.changeDetail.category')}</dt><dd aria-label="change-category">{change.changeCategory}</dd>
        <dt>{t('m32.changeDetail.priority')}</dt><dd aria-label="change-priority">{change.priority}</dd>
        <dt>{t('m32.changeDetail.risk')}</dt><dd aria-label="change-risk">{change.riskRating ?? '—'}</dd>
        <dt>{t('m32.changeDetail.emergency')}</dt><dd aria-label="change-emergency">{change.isEmergency ? 'Yes' : 'No'}</dd>
        <dt>{t('m32.changeDetail.windowStart')}</dt><dd aria-label="change-window-start">{at(change.scheduledStartAt)}</dd>
        <dt>{t('m32.changeDetail.windowEnd')}</dt><dd aria-label="change-window-end">{at(change.scheduledEndAt)}</dd>
        <dt>{t('m32.changeDetail.status')}</dt><dd aria-label="change-status">{change.status}</dd>
      </dl>

      {change.description && <p aria-label="change-description">{change.description}</p>}

      {change.rollbackPlan && (
        <section aria-label="change-rollback">
          <h2>{t('m32.changeDetail.rollbackTitle')}</h2>
          <p>{change.rollbackPlan}</p>
        </section>
      )}

      <section aria-label="change-approvals">
        <h2>{t('m32.changeDetail.approvalsTitle')}</h2>
        {actionError && <p role="alert" aria-label="approval-error">{actionError}</p>}
        {notice && <p aria-label="approval-notice">{notice}</p>}
        {approvals.length > 0 ? (
          <table aria-label="approval-table">
            <thead><tr><th>{t('m32.changeDetail.code')}</th><th>{t('m32.changeDetail.thLevel')}</th><th>{t('m32.changeDetail.status')}</th><th>{t('m32.changeDetail.thComment')}</th><th>{t('m32.changeDetail.thActions')}</th></tr></thead>
            <tbody>
              {approvals.map((a) => (
                <tr key={a.approvalId} data-status={a.status}>
                  <td>{a.approvalCode}</td><td>{a.approvalLevel}</td><td>{a.status}</td>
                  <td>{a.decisionComment ?? '—'}</td>
                  <td>
                    {a.status === 'PENDING' && (
                      <>
                        <input aria-label={`comment-${a.approvalId}`} placeholder={t('m32.changeDetail.commentPlaceholder')} value={comment[a.approvalId] ?? ''} onChange={(e) => setComment((m) => ({ ...m, [a.approvalId]: e.target.value }))} />
                        <button type="button" aria-label={`approve-${a.approvalId}`} onClick={() => void decide(a, 'approve', t('m32.changeDetail.approvedLabel'))}>{t('m32.changeDetail.approve')}</button>
                        <button type="button" aria-label={`reject-${a.approvalId}`} onClick={() => void decide(a, 'reject', t('m32.changeDetail.rejectedLabel'))}>{t('m32.changeDetail.reject')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p aria-label="change-no-approvals">{t('m32.changeDetail.noApprovals')}</p>
        )}
      </section>
    </main>
  );
}
